import { FunctionComponent } from 'react';
import { Box, Button, Chip, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { IMfaOrgUser } from '../../../shared/services/mfa/mfa.model';

// Fixed column widths (tableLayout: 'fixed') so the Name and Email columns line
// up across the separate tables shown per organisation on the partner page. Only
// the trailing action column is left unsized, so it absorbs any leftover width
// while Name/Email/Method stay identical everywhere.
const NAME_WIDTH = 200;
const EMAIL_WIDTH = 260;
const METHOD_WIDTH = 150;
const ellipsisCell = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const;

// Shared read-out of each user's current second factor, with the admin/partner
// "set to email" action for anyone on the authenticator app. A compact MUI table
// rather than the heavy sortable TableDisplay grid, matching the plain-MUI style
// of the admin user detail page.
const methodChip = (user: IMfaOrgUser) => {
  if (user.isUsingSingleSignOn) {
    return <Chip size="small" label="SSO" variant="outlined" />;
  }
  if (user.method === 'app') {
    return <Chip size="small" color="success" label="Authenticator app" />;
  }
  if (user.method === 'email') {
    return <Chip size="small" color="primary" label="Email" />;
  }
  return <Chip size="small" variant="outlined" label="None" />;
};

export const MfaUserTable: FunctionComponent<{
  users: IMfaOrgUser[],
  onSetToEmail: (userId: string) => void,
  busy?: boolean,
}> = ({ users, onSetToEmail, busy }) => {
  if (!users.length) {
    return <Typography variant="body2" sx={{ color: 'text.secondary' }}>No users yet.</Typography>;
  }
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 700 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: NAME_WIDTH }}>Name</TableCell>
            <TableCell sx={{ width: EMAIL_WIDTH }}>Email</TableCell>
            <TableCell sx={{ width: METHOD_WIDTH }}>Method</TableCell>
            <TableCell align="right">Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((user) => {
            const name = `${user.firstName} ${user.surname}`.trim();
            return (
              <TableRow key={user.id}>
                <TableCell sx={ellipsisCell} title={name}>{name}</TableCell>
                <TableCell sx={ellipsisCell} title={user.email}>{user.email}</TableCell>
                <TableCell>{methodChip(user)}</TableCell>
                <TableCell align="right">
                  {user.method === 'app' && !user.isUsingSingleSignOn ? (
                    <Button size="small" variant="outlined" disabled={busy} onClick={() => onSetToEmail(user.id)}>
                      <Typography sx={{ px: 0.5, fontSize: '0.8125rem' }}>Set to email</Typography>
                    </Button>
                  ) : (
                    <Typography variant="body2" sx={{ color: 'text.disabled', fontSize: '0.8125rem' }}>None available</Typography>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};
